import DOMPurify from "dompurify";
import lodash from "lodash";
import { C } from "../ui/constructors";
export class TemplateResolver {
    constructor(...functions) {
        this.templates = {};
        for (let f of functions) {
            this.templates[f.name] = f;
        }
    }
    with(...functions) {
        let copy = this.copy();
        for (let f of functions) {
            copy.templates[f.name] = f;
        }
        return copy;
    }
    resolve(template) {
        const expression = TemplateResolver.Expression.parse(template, this.templates);
        const result = TemplateResolver.Expression.evaluate(expression).map(TemplateResolver.Value.asAppendable);
        return result;
    }
    copy() {
        return new TemplateResolver(...Object.values(this.templates));
    }
}
(function (TemplateResolver) {
    var span = C.span;
    TemplateResolver.ExpressionType = ["safestring", "application", "domelement", "error"];
    let Value;
    (function (Value) {
        function asAppendable(val) {
            switch (val.type) {
                case "safestring":
                    return val.value;
                case "domelement":
                    return val.value;
                case "error":
                    return "ERROR";
            }
        }
        Value.asAppendable = asAppendable;
    })(Value = TemplateResolver.Value || (TemplateResolver.Value = {}));
    TemplateResolver.Concat = {
        name: "concat",
        apply: args => {
            const result = [];
            let concat_buffer = [];
            const contains_dom = args.some(a => a.type == "domelement");
            function clear() {
                if (concat_buffer.length > 0) {
                    let s = result.length > 0 ? "&nbsp;" : "";
                    s += concat_buffer.map(e => e.value).join(" ");
                    s += "&nbsp;";
                    if (contains_dom) {
                        result.push({ type: "domelement", value: span().setInnerHtml(s) });
                    }
                    else {
                        result.push({ type: "safestring", value: s });
                    }
                    concat_buffer = [];
                }
            }
            for (const arg of args) {
                switch (arg.type) {
                    case "safestring":
                        concat_buffer.push(arg);
                        break;
                    case "domelement":
                        clear();
                        result.push(arg);
                        break;
                }
            }
            clear();
            return result;
        }
    };
    TemplateResolver.Unknown = {
        name: "Unknown",
        apply: () => [{ type: "safestring", value: "UNKNOWN FUNCTION" }]
    };
    let Expression;
    (function (Expression) {
        function evaluate(exp) {
            try {
                switch (exp.type) {
                    case "application":
                        const evaluated_args = exp.arguments.flatMap(a => evaluate(a));
                        return (exp.function ?? TemplateResolver.Unknown).apply(evaluated_args);
                    default:
                        return [exp];
                }
            }
            catch (e) {
                return [{ type: "error", message: e.toString() }];
            }
        }
        Expression.evaluate = evaluate;
        function tokenize(input) {
            let tokens = [];
            let i = 0;
            while (i < input.length) {
                if (input.startsWith("{{", i)) {
                    tokens.push("open");
                    i += 2;
                }
                else if (input.startsWith("}}", i)) {
                    tokens.push("close");
                    i += 2;
                }
                else {
                    let next_space = Math.min(...[
                        input.indexOf(" ", i),
                        input.indexOf("{{", i),
                        input.indexOf("}}", i),
                    ].filter(i => i > 0));
                    if (next_space < 0)
                        next_space = input.length;
                    tokens.push({
                        type: "safestring",
                        value: DOMPurify.sanitize(lodash.escape(input.substring(i, next_space).trimEnd()))
                    });
                    i = next_space;
                }
                while (input.charAt(i) == " ")
                    i++;
            }
            return tokens;
        }
        function parse(input, table) {
            const tokens = tokenize(input);
            let lookahead_index = 0;
            function getLookahead() {
                return tokens[lookahead_index];
            }
            function parse_application() {
                let lookahead = getLookahead();
                if (!lookahead || lookahead == "open" || lookahead == "close")
                    return { type: "application", function: TemplateResolver.Concat, arguments: [] };
                const name = DOMPurify.sanitize(lodash.escape(lookahead.value));
                lookahead_index += 1;
                const f = table[name];
                if (f)
                    return {
                        type: "application",
                        function: f,
                        arguments: parse_arguments()
                    };
                else
                    return {
                        type: "error",
                        message: `Unknown Function ${name}`
                    };
            }
            function parse_arguments() {
                const list = [];
                while (true) {
                    const lookahead = getLookahead();
                    if (lookahead == "close" || lookahead == undefined) {
                        lookahead_index += 1; // Consume close token
                        return list;
                    }
                    else if (lookahead == "open") {
                        lookahead_index += 1;
                        list.push(parse_application());
                    }
                    else {
                        list.push(lookahead);
                        lookahead_index += 1;
                    }
                }
            }
            const args = parse_arguments();
            return {
                type: "application",
                function: TemplateResolver.Concat,
                arguments: args
            };
        }
        Expression.parse = parse;
    })(Expression = TemplateResolver.Expression || (TemplateResolver.Expression = {}));
})(TemplateResolver || (TemplateResolver = {}));
